import { supabase } from "@/integrations/supabase/client";

export interface ICD11Entry {
  id: string;
  icd_code: string;
  title: string;
  definition?: string;
  inclusion_criteria?: string[];
  exclusion_criteria?: string[];
  parent_code?: string;
  child_codes?: string[];
  medical_category?: string;
  symptom_mappings?: string[];
  severity_level?: 'low' | 'medium' | 'high';
  body_system?: string;
  is_active?: boolean;
  metadata?: any;
}

export class ICD11Service {
  // Search ICD-11 entries by symptoms
  static async searchBySymptoms(symptoms: string[]): Promise<ICD11Entry[]> {
    try {
      if (!symptoms.length) return [];

      console.log('Searching ICD-11 for symptoms:', symptoms);

      // Use GIN index for efficient array overlap search
      const { data, error } = await supabase
        .from('icd11_knowledge_base')
        .select('*')
        .overlaps('symptom_mappings', symptoms)
        .eq('is_active', true)
        .order('severity_level', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error searching ICD-11 by symptoms:', error);
        return [];
      }

      return (data || []) as ICD11Entry[];
    } catch (error) {
      console.error('Error in searchBySymptoms:', error);
      return [];
    }
  }

  // Get ICD-11 entries by body system
  static async getByBodySystem(bodySystem: string): Promise<ICD11Entry[]> {
    try {
      const { data, error } = await supabase
        .from('icd11_knowledge_base')
        .select('*')
        .eq('body_system', bodySystem)
        .eq('is_active', true)
        .order('severity_level', { ascending: false })
        .limit(15);

      if (error) {
        console.error('Error fetching ICD-11 by body system:', error);
        return [];
      }

      return (data || []) as ICD11Entry[];
    } catch (error) {
      console.error('Error in getByBodySystem:', error);
      return [];
    }
  }

  // Get ICD-11 entries by medical category
  static async getByMedicalCategory(category: string): Promise<ICD11Entry[]> {
    try {
      const { data, error } = await supabase
        .from('icd11_knowledge_base')
        .select('*')
        .eq('medical_category', category)
        .eq('is_active', true)
        .order('severity_level', { ascending: false })
        .limit(15);

      if (error) {
        console.error('Error fetching ICD-11 by category:', error);
        return [];
      }

      return (data || []) as ICD11Entry[];
    } catch (error) {
      console.error('Error in getByMedicalCategory:', error);
      return [];
    }
  }

  // Get specific ICD-11 entry by code
  static async getByCode(icdCode: string): Promise<ICD11Entry | null> {
    try {
      const { data, error } = await supabase
        .from('icd11_knowledge_base')
        .select('*')
        .eq('icd_code', icdCode)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching ICD-11 by code:', error);
        return null;
      }

      return data as ICD11Entry;
    } catch (error) {
      console.error('Error in getByCode:', error);
      return null;
    }
  }

  // Map questionnaire responses to ICD-11 codes
  static async mapResponsesToICD11(responses: {
    field: string;
    symptoms: string[];
    duration: string;
    pattern: string;
    impact: number;
  }): Promise<{
    primaryConditions: ICD11Entry[];
    secondaryConditions: ICD11Entry[];
    symptomMappings: Array<{ symptom: string; icdEntries: ICD11Entry[] }>;
  }> {
    try {
      console.log('Mapping questionnaire responses to ICD-11:', responses);

      // 1. Search by symptoms
      const symptomResults = await this.searchBySymptoms(responses.symptoms);

      // 2. Filter by body system based on field
      const bodySystemMap: Record<string, string> = {
        'chest/breathing': 'respiratory',
        'muscle/bones': 'musculoskeletal',
        'stomach/digestion': 'digestive',
        'mental health': 'mental_behavioral',
        'skin': 'integumentary',
        'head/neurological': 'nervous'
      };

      const bodySystem = bodySystemMap[responses.field];
      let bodySystemResults: ICD11Entry[] = [];
      if (bodySystem) {
        bodySystemResults = await this.getByBodySystem(bodySystem);
      }

      // 3. Combine and rank results
      const allResults = [...symptomResults, ...bodySystemResults];
      const uniqueResults = allResults.reduce((acc, current) => {
        const existing = acc.find(item => item.icd_code === current.icd_code);
        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, [] as ICD11Entry[]);

      // 4. Score and rank conditions
      const scoredResults = uniqueResults.map(entry => ({
        ...entry,
        relevanceScore: this.calculateRelevanceScore(entry, responses)
      })).sort((a, b) => b.relevanceScore - a.relevanceScore);

      // 5. Separate primary and secondary conditions
      const primaryConditions = scoredResults.slice(0, 5);
      const secondaryConditions = scoredResults.slice(5, 10);

      // 6. Create symptom mappings
      const symptomMappings = await Promise.all(
        responses.symptoms.map(async (symptom) => {
          const mappings = await this.searchBySymptoms([symptom]);
          return {
            symptom,
            icdEntries: mappings.slice(0, 3)
          };
        })
      );

      return {
        primaryConditions,
        secondaryConditions,
        symptomMappings
      };
    } catch (error) {
      console.error('Error in mapResponsesToICD11:', error);
      return {
        primaryConditions: [],
        secondaryConditions: [],
        symptomMappings: []
      };
    }
  }

  // Calculate relevance score for ranking
  private static calculateRelevanceScore(entry: ICD11Entry, responses: any): number {
    let score = 0;

    // Base score by severity
    if (entry.severity_level === 'high') score += 30;
    else if (entry.severity_level === 'medium') score += 20;
    else score += 10;

    // Symptom matching bonus
    const matchingSymptoms = responses.symptoms.filter((symptom: string) =>
      entry.symptom_mappings?.some(mapping => 
        mapping.toLowerCase().includes(symptom.toLowerCase()) ||
        symptom.toLowerCase().includes(mapping.toLowerCase())
      )
    ).length;
    score += matchingSymptoms * 15;

    // Duration consideration
    if (responses.duration.includes('acute') || responses.duration.includes('hours')) {
      if (entry.title.toLowerCase().includes('acute')) score += 20;
    } else if (responses.duration.includes('chronic') || responses.duration.includes('months')) {
      if (entry.title.toLowerCase().includes('chronic')) score += 20;
    }

    // Impact consideration
    if (responses.impact >= 8 && entry.severity_level === 'high') score += 25;
    else if (responses.impact >= 6 && entry.severity_level === 'medium') score += 15;
    else if (responses.impact <= 3 && entry.severity_level === 'low') score += 10;

    return score;
  }

  // Get ICD-11 statistics
  static async getStatistics(): Promise<{
    totalEntries: number;
    byBodySystem: Record<string, number>;
    bySeverity: Record<string, number>;
    lastUpdated: string | null;
  }> {
    try {
      // Get total count
      const { count: totalEntries } = await supabase
        .from('icd11_knowledge_base')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Get counts by body system
      const { data: bodySystemData } = await supabase
        .from('icd11_knowledge_base')
        .select('body_system')
        .eq('is_active', true);

      const byBodySystem = bodySystemData?.reduce((acc, item) => {
        const system = item.body_system || 'unknown';
        acc[system] = (acc[system] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Get counts by severity
      const { data: severityData } = await supabase
        .from('icd11_knowledge_base')
        .select('severity_level')
        .eq('is_active', true);

      const bySeverity = severityData?.reduce((acc, item) => {
        const severity = item.severity_level || 'unknown';
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Get last updated
      const { data: lastUpdateData } = await supabase
        .from('icd11_knowledge_base')
        .select('updated_at')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1);

      return {
        totalEntries: totalEntries || 0,
        byBodySystem,
        bySeverity,
        lastUpdated: lastUpdateData?.[0]?.updated_at || null
      };
    } catch (error) {
      console.error('Error getting ICD-11 statistics:', error);
      return {
        totalEntries: 0,
        byBodySystem: {},
        bySeverity: {},
        lastUpdated: null
      };
    }
  }
}